import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { PLANO_LABELS, fmtPct, monthLabel, toLastClosedMonthRef, STATUS_LABELS, statusColor, CLIMA_LABELS } from "@/lib/format";
import { Download, FileText } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

export default function Report() {
    const [mes, setMes] = useState(toLastClosedMonthRef()); // M8 — abre no último mês fechado
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tableFilter, setTableFilter] = useState(null); // item 6 — {status, periodo}

    async function load(refMes = mes) {
        setLoading(true);
        setTableFilter(null);
        try {
            const r = await api.get(`/report?mes=${refMes}`);
            setData(r.data);
        } finally { setLoading(false); }
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const filteredRows = data
        ? (tableFilter
            ? data.rows.filter(r => (tableFilter.periodo === "atual" ? r.status : r.status_prev) === tableFilter.status)
            : data.rows)
        : [];

    return (
        <div className="space-y-6 fade-in" data-testid="report-page">
            <header className="flex items-end justify-between no-print">
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium">Relatórios</div>
                    <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">Relatório Gerencial</h1>
                    <p className="text-sm text-slate-500 mt-1">Visão consolidada de uso, score e status por cliente no período selecionado.</p>
                </div>
                <div className="flex items-end gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Mês de referência</label>
                        <input type="month" value={mes} onChange={(e) => setMes(e.target.value)}
                            data-testid="report-mes-input"
                            className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none" />
                        <div className="text-[10px] text-slate-400 mt-1">Padrão: último mês encerrado</div>
                    </div>
                    <button onClick={() => load()} data-testid="report-load-button" className="text-sm px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50 font-semibold inline-flex items-center gap-1">
                        Atualizar
                    </button>
                    <button onClick={() => window.print()} data-testid="report-print-button" className="text-sm px-4 py-2 bg-[#0F2044] hover:bg-[#172E5E] text-white rounded-md font-semibold inline-flex items-center gap-2">
                        <Download className="h-4 w-4" /> Exportar PDF
                    </button>
                </div>
            </header>

            {loading || !data ? (
                <div className="text-sm text-slate-500">Carregando relatório…</div>
            ) : (
                <div className="space-y-6">
                    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="report-summary-card">
                        <div className="flex items-center gap-2 mb-4">
                            <FileText className="h-4 w-4 text-[#0F2044]" />
                            <h2 className="font-display font-semibold text-lg text-[#0F2044]">
                                Sumário executivo · {monthLabel(data.mes)} <span className="text-slate-400 text-base font-normal">(vs {monthLabel(data.mes_anterior)})</span>
                            </h2>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-center">
                            <Sumario label="Mensagens" value={data.sumario.total_mensagens} variation={data.sumario.variacao_mensagens} />
                            <Sumario label="Documentos" value={data.sumario.total_documentos} variation={data.sumario.variacao_documentos} />
                            <Sumario label="Analisador" value={data.sumario.total_analises} variation={data.sumario.variacao_analises} />
                            <Sumario label="Total interações" value={data.sumario.total_interacoes} variation={data.sumario.variacao_total} accent />
                        </div>
                    </div>

                    {/* M1 — gráficos de status no topo do relatório (item 6 — clicáveis para filtrar a tabela) */}
                    {data.status_dist && (
                        <StatusDistCard dist={data.status_dist} mes={data.mes} mesAnterior={data.mes_anterior}
                            activeFilter={tableFilter}
                            onFilter={(status, periodo) =>
                                setTableFilter(prev => (prev && prev.status === status && prev.periodo === periodo) ? null : { status, periodo })
                            } />
                    )}

                    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="report-table-card">
                        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                            <h3 className="font-display font-semibold text-base text-[#0F2044]">Tabela consolidada por cliente</h3>
                            {tableFilter && (
                                <div className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1.5"
                                    style={{ background: statusColor(tableFilter.status) + "22", color: statusColor(tableFilter.status) }}
                                    data-testid="report-table-filter-chip">
                                    Filtro: {STATUS_LABELS[tableFilter.status]} · {tableFilter.periodo === "atual" ? monthLabel(data.mes) : monthLabel(data.mes_anterior)}
                                    <span className="text-slate-400">({filteredRows.length})</span>
                                    <button onClick={() => setTableFilter(null)} data-testid="report-table-filter-clear"
                                        className="ml-1 hover:opacity-70">✕</button>
                                </div>
                            )}
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">
                                    <th className="py-2 pr-3 font-medium">Cliente</th>
                                    <th className="py-2 pr-3 font-medium">Plano</th>
                                    <th className="py-2 pr-3 font-medium text-right">Mensagens</th>
                                    <th className="py-2 pr-3 font-medium text-right">Documentos</th>
                                    <th className="py-2 pr-3 font-medium text-right">Analisador</th>
                                    <th className="py-2 pr-3 font-medium text-right">Total</th>
                                    <th className="py-2 pr-3 font-medium text-right">Var.</th>
                                    <th className="py-2 pr-3 font-medium text-right">Score</th>
                                    <th className="py-2 pr-3 font-medium">Clima</th>
                                    <th className="py-2 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.length === 0 && (
                                    <tr><td colSpan={10} className="py-6 text-center text-slate-400 text-sm">Nenhum cliente neste filtro.</td></tr>
                                )}
                                {filteredRows.map(r => (
                                    <tr key={r.contract_id} className="border-b border-slate-50" data-testid={`report-row-${r.contract_id}`}>
                                        <td className="py-2 pr-3 font-medium text-[#0F2044]">
                                            <Link to={`/contratos/${r.contract_id}`}
                                                data-testid={`report-client-link-${r.contract_id}`}
                                                className="hover:underline hover:text-[#172E5E] cursor-pointer">
                                                {r.organizacao_nome}
                                            </Link>
                                            {r.novo_cliente && (
                                                <span data-testid={`novo-cliente-badge-${r.contract_id}`}
                                                    className="ml-2 badge-status" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>
                                                    Novo cliente
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-3 text-slate-700">{PLANO_LABELS[r.plano]}</td>
                                        <td className="py-2 pr-3 text-right font-mono-tab">{r.mensagens}</td>
                                        <td className="py-2 pr-3 text-right font-mono-tab">{r.documentos}</td>
                                        <td className="py-2 pr-3 text-right font-mono-tab">{r.analises}</td>
                                        <td className="py-2 pr-3 text-right font-mono-tab font-semibold">{r.total}</td>
                                        <td className={`py-2 pr-3 text-right font-mono-tab ${r.variacao != null ? (r.variacao >= 0 ? "text-emerald-600" : "text-red-600") : ""}`}>
                                            {r.variacao != null ? fmtPct(r.variacao, { signed: true }) : "—"}
                                        </td>
                                        <td className="py-2 pr-3 text-right font-mono-tab font-semibold">{r.score}</td>
                                        <td className="py-2 pr-3 text-slate-700">{r.clima ? CLIMA_LABELS[r.clima] : <span className="text-slate-400">—</span>}</td>
                                        <td className="py-2"><StatusBadge status={r.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                        <Highlights title="Destaques de crescimento" rows={data.destaques_crescimento} positive testid="report-growth" />
                        <Highlights title="Pontos de atenção" rows={data.pontos_atencao} positive={false} testid="report-attention" />
                    </div>
                </div>
            )}
        </div>
    );
}

const STATUS_ORDER = ["excelente", "saudavel", "atencao", "critico"];

// M8 — pizzas lado a lado + quadro de mudanças (item 6 — clicáveis p/ filtrar a tabela)
function StatusDistCard({ dist, mes, mesAnterior, onFilter, activeFilter }) {
    const toPieData = (d) => STATUS_ORDER
        .map(s => ({ name: STATUS_LABELS[s], status: s, value: d[s] || 0 }))
        .filter(x => x.value > 0);
    const dataAnterior = toPieData(dist.anterior);
    const dataAtual = toPieData(dist.atual);

    const pp = (s) => {
        const pAtual = dist.base_atual ? (dist.atual[s] || 0) / dist.base_atual * 100 : 0;
        const pAnt = dist.base_anterior ? (dist.anterior[s] || 0) / dist.base_anterior * 100 : 0;
        return pAtual - pAnt;
    };

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="report-status-chart">
            <h3 className="font-display font-semibold text-base text-[#0F2044] mb-1">
                Status dos clientes — {monthLabel(mesAnterior)} x {monthLabel(mes)}
            </h3>
            <p className="text-xs text-slate-500 mb-4">Clique em um status (fatia ou legenda) para filtrar a tabela consolidada por período.</p>
            <div className="grid grid-cols-2 gap-6">
                <StatusPie title={`Mês anterior · ${monthLabel(mesAnterior)}`} data={dataAnterior} base={dist.base_anterior} testid="pie-anterior"
                    periodo="anterior" onFilter={onFilter} activeFilter={activeFilter} />
                <StatusPie title={`Atual · ${monthLabel(mes)}`} data={dataAtual} base={dist.base_atual} testid="pie-atual"
                    periodo="atual" onFilter={onFilter} activeFilter={activeFilter} />
            </div>
            <div className="mt-6" data-testid="report-status-changes">
                <h4 className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Mudanças</h4>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-slate-200">
                            <th className="py-2 pr-3 font-medium">Status</th>
                            <th className="py-2 pr-3 font-medium text-right">{monthLabel(mesAnterior)}</th>
                            <th className="py-2 pr-3 font-medium text-right">{monthLabel(mes)}</th>
                            <th className="py-2 pr-3 font-medium text-right">Variação</th>
                            <th className="py-2 font-medium text-right">p.p.</th>
                        </tr>
                    </thead>
                    <tbody>
                        {STATUS_ORDER.map(s => {
                            const ant = dist.anterior[s] || 0;
                            const atu = dist.atual[s] || 0;
                            const diff = atu - ant;
                            const dpp = pp(s);
                            return (
                                <tr key={s} className="border-b border-slate-50" data-testid={`status-change-${s}`}>
                                    <td className="py-2 pr-3">
                                        <span className="inline-flex items-center gap-2">
                                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: statusColor(s) }} />
                                            {STATUS_LABELS[s]}
                                        </span>
                                    </td>
                                    <td className="py-2 pr-3 text-right font-mono-tab">{ant}</td>
                                    <td className="py-2 pr-3 text-right font-mono-tab font-semibold">{atu}</td>
                                    <td className={`py-2 pr-3 text-right font-mono-tab font-semibold ${diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-600" : "text-slate-400"}`}>
                                        {diff > 0 ? `+${diff}` : diff}
                                    </td>
                                    <td className={`py-2 text-right font-mono-tab ${dpp > 0 ? "text-emerald-600" : dpp < 0 ? "text-red-600" : "text-slate-400"}`}>
                                        {dpp > 0 ? "+" : ""}{dpp.toFixed(1)} p.p.
                                    </td>
                                </tr>
                            );
                        })}
                        <tr data-testid="status-change-base">
                            <td className="py-2 pr-3 font-semibold text-[#0F2044]">Base total de clientes</td>
                            <td className="py-2 pr-3 text-right font-mono-tab">{dist.base_anterior}</td>
                            <td className="py-2 pr-3 text-right font-mono-tab font-semibold">{dist.base_atual}</td>
                            <td className={`py-2 pr-3 text-right font-mono-tab font-semibold ${dist.base_atual - dist.base_anterior > 0 ? "text-emerald-600" : dist.base_atual - dist.base_anterior < 0 ? "text-red-600" : "text-slate-400"}`}>
                                {dist.base_atual - dist.base_anterior > 0 ? `+${dist.base_atual - dist.base_anterior}` : dist.base_atual - dist.base_anterior}
                            </td>
                            <td className="py-2 text-right text-slate-400">—</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatusPie({ title, data, base, testid, periodo, onFilter, activeFilter }) {
    const total = data.reduce((acc, d) => acc + d.value, 0);
    const clickable = typeof onFilter === "function";
    const handle = (status) => { if (clickable && status) onFilter(status, periodo); };
    return (
        <div className="text-center" data-testid={testid}>
            <div className="text-xs font-semibold text-slate-600 mb-1">{title}</div>
            {total === 0 ? (
                <div className="h-[200px] flex items-center justify-center text-sm text-slate-400">Sem clientes no período.</div>
            ) : (
                <div style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40}
                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                labelLine={false} fontSize={11} isAnimationActive={false}
                                onClick={(d) => handle(d?.status || d?.payload?.status)}
                                style={{ cursor: clickable ? "pointer" : "default" }}>
                                {data.map(d => <Cell key={d.status} fill={statusColor(d.status)} style={{ cursor: clickable ? "pointer" : "default" }} />)}
                            </Pie>
                            <Tooltip formatter={(v, n) => [`${v} cliente(s)`, n]} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            )}
            <div className="text-[11px] text-slate-500 mt-1">Base: <strong>{base}</strong> cliente(s)</div>
            {clickable && total > 0 && (
                <div className="flex flex-wrap justify-center gap-1.5 mt-2" data-testid={`${testid}-legend`}>
                    {data.map(d => {
                        const active = activeFilter && activeFilter.status === d.status && activeFilter.periodo === periodo;
                        return (
                            <button key={d.status} onClick={() => handle(d.status)}
                                data-testid={`${testid}-legend-${d.status}`}
                                className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-1 border transition-colors ${active ? "border-transparent text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                                style={active ? { background: statusColor(d.status) } : {}}>
                                <span className="h-2 w-2 rounded-full" style={{ background: active ? "#fff" : statusColor(d.status) }} />
                                {d.name} ({d.value})
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Sumario({ label, value, variation, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
            <div className={`mt-1 font-display font-semibold ${accent ? "text-3xl text-[#0F2044]" : "text-2xl text-[#0F2044]"}`}>
                {value.toLocaleString("pt-BR")}
            </div>
            <div className={`text-xs font-medium mt-1 ${variation == null ? "text-slate-400" : variation >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {variation == null ? "—" : fmtPct(variation, { signed: true }) + " vs mês anterior"}
            </div>
        </div>
    );
}

function Highlights({ title, rows, positive, testid }) {
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid={testid}>
            <h3 className={`font-display font-semibold text-base ${positive ? "text-emerald-700" : "text-red-700"}`}>{title}</h3>
            {rows.length === 0 ? (
                <div className="text-sm text-slate-500 py-3">Sem registros neste mês.</div>
            ) : (
                <ul className="divide-y divide-slate-100 mt-3">
                    {rows.map((r) => (
                        <li key={r.contract_id} className="py-2 flex items-center justify-between text-sm">
                            <span className="text-[#0F2044] font-medium">{r.organizacao_nome}</span>
                            <span className={`font-mono-tab font-semibold ${positive ? "text-emerald-600" : "text-red-600"}`}>
                                {fmtPct(r.variacao, { signed: true })}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
